import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { resendEmailConfirmation } from '../services/supabase';
import { useNotification } from '../hooks/useNotification';
import Navbar from '../components/layout/Navbar';
import Button from '../components/common/Button';
import { Mail } from 'lucide-react';
import './EmailConfirmationPage.css';

function EmailConfirmationPage() {
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [isResending, setIsResending] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(60);
  const [searchParams] = useSearchParams();
  // Start initial cooldown on mount
  useEffect(() => {
    if (cooldownRemaining <= 0) return;
    const intervalId = setInterval(() => {
      setCooldownRemaining((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(intervalId);
  }, [cooldownRemaining]);

  // Get email from URL params or user object
  const email = searchParams.get('email') || user?.email;

  const handleResendEmail = async () => {
    if (!email) {
      showError('No email address found. Please refresh the page and try again.');
      return;
    }

    setIsResending(true);

    try {
      const { error } = await resendEmailConfirmation(email);
      if (error) {
        // If it's a user exists style error, we still treat as success since email likely sent
        if ((error.message || '').toLowerCase().includes('already registered')) {
          showSuccess('Confirmation email has been resent! Please check your inbox.');
        } else {
          showError(`Failed to resend confirmation email: ${error.message}`);
        }
      } else {
        showSuccess('Confirmation email has been resent! Please check your inbox.');
      }
    } catch (error) {
      console.error('Resend confirmation error:', error);
      showError('Failed to resend confirmation email. Please try again.');
    } finally {
      setIsResending(false);
      // Restart cooldown after attempting resend
      setCooldownRemaining(60);
    }
  };

  return (
    <div className="email-confirmation-page">
      <Navbar />

      <div className="email-confirmation-content">
        <div className="confirmation-container">
          <h1>Almost There!</h1>

          <p className="confirmation-message">
            We've sent a confirmation email to <strong>{email}</strong>
          </p>

          <p className="confirmation-instructions">
            Check your inbox and click the confirmation link to get started!
          </p>

          <div className="confirmation-actions">
            <Button
              variant="primary"
              className="resend-btn"
              onClick={handleResendEmail}
              disabled={isResending || cooldownRemaining > 0}
              isLoading={isResending}
              fullWidth
              icon={Mail}
            >
              {isResending
                ? 'Sending...'
                : cooldownRemaining > 0
                  ? `Resend Email (${cooldownRemaining}s)`
                  : 'Resend Email'}
            </Button>
          </div>

          <div className="help-text">
            <p>
              <strong>Can't find the email?</strong>
              <br></br>Check your spam or junk folder, double-check that you entered the correct
              email address, give it a moment - emails can take a few minutes to arrive, or use the
              resend button above if you need another copy.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmailConfirmationPage;
